/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import AddIcon from "@material-ui/icons/Add";
import EventNoteIcon from "@material-ui/icons/EventNote";

import MainHeader from "../../components/MainHeader";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  pageHeader: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 16,
    color: "#1e2a44",
    background: "#EDF4FF",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  pageHeaderTitle: {
    fontWeight: 600,
    letterSpacing: 0.1,
    fontSize: "1.2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.05rem",
    },
  },
  pageHeaderSubtitle: {
    marginTop: theme.spacing(0.25),
    color: "rgba(30,42,68,0.78)",
    fontSize: "0.8rem",
    lineHeight: 1.35,
  },
  headerChip: {
    backgroundColor: "#EAF1FF",
    color: "#2f4b7c",
    border: "1px solid #d7e5ff",
    fontWeight: 500,
    fontSize: "0.72rem",
    height: 24,
  },
  controlsPaper: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
    padding: theme.spacing(1.25),
    backgroundColor: theme.palette.background.paper,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiInputBase-input": {
      fontSize: "0.82rem",
      paddingTop: 13,
      paddingBottom: 13,
    },
  },
  actionButton: {
    minHeight: 42,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.75rem",
    padding: theme.spacing(0.8, 1.4),
    boxShadow: "0 6px 14px rgba(7, 64, 171, 0.14)",
  },
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 12px 26px rgba(17, 24, 39, 0.09)",
  },
  tableHeaderCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(15, 23, 42, 0.03)",
    fontSize: "0.76rem",
  },
  tableRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(15, 23, 42, 0.035)",
    },
  },
  tableCellText: {
    fontSize: "0.78rem",
  },
  actionCell: {
    minWidth: 170,
  },
  actionIconButton: {
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0, 0.2),
  },

  premiumRoot: {
    width: "100%",
    maxWidth: "100%",
    minHeight: "100%",
    height: "auto",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(7),
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
      : "linear-gradient(145deg, #EEF5FF 0%, #F8FAFC 100%)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.2),
      paddingBottom: theme.spacing(8)
    }
  },
  campaignHero: {
    position: "relative",
    overflow: "visible",
    borderRadius: 24,
    padding: theme.spacing(2.1),
    background: "linear-gradient(135deg, #071B4D 0%, #005BFF 58%, #00A3FF 100%)",
    color: "#fff",
    boxShadow: "0 24px 58px rgba(0,102,255,0.22)",
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: theme.spacing(2),
    alignItems: "center",
    maxWidth: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    },
    [theme.breakpoints.down("sm")]: {
      borderRadius: 22,
      padding: theme.spacing(1.8)
    }
  },
  heroGlow: {
    position: "absolute",
    right: -80,
    top: -80,
    width: 240,
    height: 240,
    borderRadius: "50%",
    background: "rgba(255,255,255,.16)",
    filter: "blur(3px)",
    pointerEvents: "none"
  },
  campaignHeroLeft: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    gap: 0,
    alignItems: "center",
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start"
    }
  },
  campaignHeroIcon: {
    width: 62,
    height: 62,
    borderRadius: 20,
    background: "rgba(255,255,255,.16)",
    color: "#fff",
    fontSize: 28,
    boxShadow: "0 14px 28px rgba(0,0,0,.18)",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  campaignHeroTitle: {
    fontWeight: 950,
    fontSize: "1.85rem",
    lineHeight: 1.06,
    letterSpacing: "-1px",
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem"
    }
  },
  campaignHeroSubtitle: {
    fontSize: ".9rem",
    lineHeight: 1.48,
    opacity: .86,
    marginTop: 8,
    maxWidth: 650,
    [theme.breakpoints.down("sm")]: {
      fontSize: ".76rem",
      lineHeight: 1.35
    }
  },
  campaignHeroActions: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      "& button": { width: "100%" }
    }
  },
  campaignHeroStats: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  campaignMetric: {
    borderRadius: 18,
    padding: theme.spacing(1.4),
    background: "rgba(255,255,255,.14)",
    border: "1px solid rgba(255,255,255,.20)",
    backdropFilter: "blur(8px)",
    minWidth: 0
  },
  campaignMetricValue: {
    fontWeight: 950,
    fontSize: "1.25rem",
    color: "#fff",
    lineHeight: 1.08,
    wordBreak: "break-word",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem"
    }
  },
  campaignMetricLabel: {
    fontSize: ".68rem",
    opacity: .78,
    fontWeight: 800,
    marginTop: 4
  },
  navHint: {
    borderRadius: 18,
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    "& span": {
      borderRadius: 999,
      padding: "8px 12px",
      background: "rgba(0,102,255,.08)",
      color: "#0066FF",
      fontSize: ".76rem",
      fontWeight: 900
    },
    [theme.breakpoints.down("sm")]: {
      "& span": { flex: 1, textAlign: "center" }
    }
  },
  riskNotice: {
    borderRadius: 22,
    padding: theme.spacing(1.6),
    background: theme.palette.type === "dark" ? "rgba(245,158,11,.10)" : "#FFFBEB",
    border: "1px solid rgba(245,158,11,.28)",
    display: "flex",
    gap: theme.spacing(1.3),
    alignItems: "flex-start",
    boxShadow: "0 12px 28px rgba(245,158,11,.08)"
  },
  riskIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "rgba(245,158,11,.16)",
    color: "#B45309",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    flexShrink: 0
  },
  riskTitle: {
    fontWeight: 950,
    fontSize: ".95rem",
    color: theme.palette.type === "dark" ? "#FDE68A" : "#92400E"
  },
  riskText: {
    fontSize: ".78rem",
    color: theme.palette.type === "dark" ? "#FCD34D" : "#92400E",
    opacity: .9,
    marginTop: 4,
    lineHeight: 1.5
  },
  premiumControls: {
    borderRadius: 22,
    padding: theme.spacing(1.4),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
    display: "grid",
    gridTemplateColumns: "1fr 220px",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  premiumSearch: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      fontSize: ".84rem",
      background: theme.palette.type === "dark" ? "rgba(255,255,255,.04)" : "#F8FAFC",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0066FF" }
    }
  },
  campaignGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
    gap: theme.spacing(1.3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  campaignCard: {
    borderRadius: 24,
    padding: theme.spacing(1.6),
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#FFFFFF",
    boxShadow: "0 14px 30px rgba(15,23,42,0.07)",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box"
  },
  campaignTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },
  campaignNameRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0
  },
  campaignAvatar: {
    width: 44,
    height: 44,
    borderRadius: 15,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    fontWeight: 950,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 22px rgba(0,102,255,.18)"
  },
  campaignName: {
    fontWeight: 950,
    fontSize: ".96rem",
    color: theme.palette.type === "dark" ? "#fff" : "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  campaignMeta: {
    fontSize: ".74rem",
    color: "#64748B",
    marginTop: 3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  campaignInfoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.4)
  },
  campaignInfoBox: {
    borderRadius: 14,
    padding: theme.spacing(1),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.035)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.06)" : "#E2E8F0"}`,
    minWidth: 0
  },
  campaignInfoLabel: {
    fontSize: ".66rem",
    fontWeight: 900,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: ".4px"
  },
  campaignInfoValue: {
    fontSize: ".78rem",
    fontWeight: 900,
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#0F172A",
    marginTop: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  campaignActions: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(.8),
    marginTop: theme.spacing(1.4)
  },
  campaignActionBtn: {
    borderRadius: 12,
    minWidth: 0,
    padding: "8px 4px",
    border: "1px solid rgba(0,102,255,.16)",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.04)" : "#F8FAFC",
    color: "#0066FF",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: ".68rem"
  },
  emptyCampaigns: {
    borderRadius: 24,
    padding: theme.spacing(5),
    textAlign: "center",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: "#94A3B8",
    fontWeight: 900
  },
  mobileScrollSpacer: {
    height: 80,
    flexShrink: 0
  }

}));

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (
        false &&
        !planConfigs.plan.useCampaigns
      ) { /* KORAX_COMMON_CAMPAIGNS */
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);

  return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [socket, user.companyId]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };


  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(c => c.status === "EM_ANDAMENTO").length;
  const scheduledCampaigns = campaigns.filter(c => c.status === "PROGRAMADA").length;
  const finishedCampaigns = campaigns.filter(c => c.status === "FINALIZADA").length;

  return (
    <div className={classes.pageRoot}>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
      {
        user.profile === "user"?
          <ForbiddenPage />
          :
          <div className={classes.premiumRoot}>
            <section className={classes.campaignHero}>
              <div className={classes.campaignHeroLeft}>
                <div style={{ minWidth: 0 }}>
                  <Typography className={classes.campaignHeroTitle}>
                    Campanhas WhatsApp
                  </Typography>
                  <Typography className={classes.campaignHeroSubtitle}>
                    Crie e acompanhe disparos com controle, segurança e clareza operacional.
                  </Typography>
                  <div className={classes.campaignHeroActions}>
                    <span style={{ background: "rgba(255,255,255,.16)", border: "1px solid rgba(255,255,255,.22)", borderRadius: 999, padding: "8px 12px", fontSize: ".76rem", fontWeight: 900 }}>
                      Envio responsável no WhatsApp
                    </span>
                  </div>
                </div>
              </div>

              <div className={classes.campaignHeroStats}>
                <div className={classes.campaignMetric}>
                  <Typography className={classes.campaignMetricValue}>{totalCampaigns}</Typography>
                  <Typography className={classes.campaignMetricLabel}>Campanhas</Typography>
                </div>
                <div className={classes.campaignMetric}>
                  <Typography className={classes.campaignMetricValue}>{activeCampaigns}</Typography>
                  <Typography className={classes.campaignMetricLabel}>Em andamento</Typography>
                </div>
                <div className={classes.campaignMetric}>
                  <Typography className={classes.campaignMetricValue}>{scheduledCampaigns}</Typography>
                  <Typography className={classes.campaignMetricLabel}>Agendadas</Typography>
                </div>
                <div className={classes.campaignMetric}>
                  <Typography className={classes.campaignMetricValue}>{finishedCampaigns}</Typography>
                  <Typography className={classes.campaignMetricLabel}>Finalizadas</Typography>
                </div>
              </div>
            </section>

            <div className={classes.riskNotice}>
              <div className={classes.riskIcon}>⚠️</div>
              <div>
                <Typography className={classes.riskTitle}>Atenção ao disparo em massa</Typography>
                <Typography className={classes.riskText}>
                  Use campanhas apenas com contatos que autorizaram ou já possuem relacionamento com sua empresa.
                  Evite volumes agressivos, mensagens repetitivas e listas frias. Disparos excessivos podem prejudicar a qualidade da conexão WhatsApp.
                </Typography>
              </div>
            </div>

            <div className={classes.premiumControls}>
              <TextField
                fullWidth
                placeholder={i18n.t("campaigns.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                className={classes.premiumSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#0066FF" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleOpenCampaignModal}
                className={classes.actionButton}
                startIcon={<AddIcon />}
                style={{ borderRadius: 14, fontWeight: 900, textTransform: "none" }}
              >
                Nova campanha
              </Button>
            </div>

            <div className={classes.campaignGrid} onScroll={handleScroll}>
              {campaigns.map((campaign) => (
                <div key={campaign.id} className={classes.campaignCard}>
                  <div className={classes.campaignTop}>
                    <div className={classes.campaignNameRow}>
                      <div className={classes.campaignAvatar}>🚀</div>
                      <div style={{ minWidth: 0 }}>
                        <Typography className={classes.campaignName}>{campaign.name}</Typography>
                        <Typography className={classes.campaignMeta}>
                          {campaign.contactListId ? campaign.contactList.name : "Lista não definida"}
                        </Typography>
                      </div>
                    </div>
                    <Chip
                      size="small"
                      label={formatStatus(campaign.status)}
                      style={{
                        fontWeight: 900,
                        fontSize: ".68rem",
                        height: 26,
                        background: "rgba(0,102,255,.08)",
                        color: "#0066FF",
                        border: "1px solid rgba(0,102,255,.18)"
                      }}
                    />
                  </div>

                  <div className={classes.campaignInfoGrid}>
                    <div className={classes.campaignInfoBox}>
                      <div className={classes.campaignInfoLabel}>WhatsApp</div>
                      <div className={classes.campaignInfoValue}>
                        {campaign.whatsappId ? campaign.whatsapp.name : "Não definido"}
                      </div>
                    </div>
                    <div className={classes.campaignInfoBox}>
                      <div className={classes.campaignInfoLabel}>Confirmação</div>
                      <div className={classes.campaignInfoValue}>
                        {campaign.confirmation ? "Habilitada" : "Desabilitada"}
                      </div>
                    </div>
                    <div className={classes.campaignInfoBox}>
                      <div className={classes.campaignInfoLabel}>Agendada</div>
                      <div className={classes.campaignInfoValue}>
                        {campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "Sem agendamento"}
                      </div>
                    </div>
                    <div className={classes.campaignInfoBox}>
                      <div className={classes.campaignInfoLabel}>Conclusão</div>
                      <div className={classes.campaignInfoValue}>
                        {campaign.completedAt ? datetimeToClient(campaign.completedAt) : "Não concluída"}
                      </div>
                    </div>
                  </div>

                  <div className={classes.campaignActions}>
                    {campaign.status === "EM_ANDAMENTO" ? (
                      <button className={classes.campaignActionBtn} onClick={() => cancelCampaign(campaign)}>Pausar</button>
                    ) : campaign.status === "CANCELADA" ? (
                      <button className={classes.campaignActionBtn} onClick={() => restartCampaign(campaign)}>Retomar</button>
                    ) : (
                      <button className={classes.campaignActionBtn} onClick={() => history.push(`/campaign/${campaign.id}/report`)}>Relatório</button>
                    )}

                    <button className={classes.campaignActionBtn} onClick={() => history.push(`/campaign/${campaign.id}/report`)}>
                      Relatório
                    </button>
                    <button className={classes.campaignActionBtn} onClick={() => handleEditCampaign(campaign)}>
                      Editar
                    </button>
                    <button className={classes.campaignActionBtn}
                      style={{ color: "#EF4444", borderColor: "rgba(239,68,68,.20)" }}
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingCampaign(campaign);
                      }}>
                      Excluir
                    </button>
                  </div>
                </div>
              ))}

              {loading && <TableRowSkeleton columns={1} />}

              {campaigns.length === 0 && !loading && (
                <div className={classes.emptyCampaigns}>
                  Nenhuma campanha encontrada.
                </div>
              )}
            </div>

            <div className={classes.mobileScrollSpacer} />
          </div>}
    </div>
  );
};

export default Campaigns;
